import { isFunction } from 'lodash/fp';

import { Route } from '@mews-distributor/common';
import { createHistoryService, EnvironmentService, HistoryService } from '@mews/framework';

import { showDates, showProperties, showRates, showSpaceCategories, showSummary } from '../actions/navigation';
import { getCurrentRoute } from '../modules/app/selectors';
import { isMultiHotel } from '../modules/configuration/selectors';
import { closeApp, handleConfirmationBackAction } from '../modules/global/actions';
import { isSelectedHotelCitySelected } from '../modules/hotels/selectors';
import { ApplicationState, DistributorThunkDispatch } from '../types';

interface DistributorHistoryServiceOptions {
    environmentService: EnvironmentService;
}

interface WithStoreHandlers {
    dispatch: DistributorThunkDispatch;
    getState: () => ApplicationState;
}

interface DistributorHistoryService {
    historyService: HistoryService;
    setStore: (handlers: WithStoreHandlers) => void;
}

const getHistoryPopMap = ({ dispatch, getState }: WithStoreHandlers) => ({
    [Route.Dates]: () => dispatch(closeApp()),
    [Route.Properties]: () => dispatch(showDates()),
    [Route.SpaceCategories]: () => dispatch(isMultiHotel(getState()) ? showProperties() : showDates()),
    [Route.Rates]: async () => dispatch(showSpaceCategories()),
    [Route.Summary]: async () => {
        if (isSelectedHotelCitySelected(getState(), undefined)) {
            dispatch(showRates());
        }

        await dispatch(showProperties());
    },
    [Route.Checkout]: () => dispatch(showSummary()),
    [Route.Confirmation]: () => dispatch(handleConfirmationBackAction()),
});

export const createDistributorHistoryService = ({
    environmentService,
}: DistributorHistoryServiceOptions): DistributorHistoryService => {
    let getState: (() => ApplicationState) | null = null;
    let dispatch: DistributorThunkDispatch | null = null;

    const historyHandler = () => {
        if (!getState || !dispatch) {
            return;
        }
        const historyPopMap = getHistoryPopMap({ dispatch, getState });
        const currentRoute = getCurrentRoute(getState());
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const handler = currentRoute && historyPopMap[currentRoute];
        if (isFunction(handler)) {
            void handler();
        }
    };

    return {
        historyService: createHistoryService({ environmentService, handler: historyHandler }),
        setStore: ({ getState: storeGetState, dispatch: storeDispatch }) => {
            dispatch = storeDispatch;
            getState = storeGetState;
        },
    };
};
